import { useCallback } from 'react';

import getConfig from 'next/config';

import { useConvertContext } from '../../../contexts/ConvertContext';
import {
  SELLER_TYPES,
  type SellerTypes,
} from '../../../components/PageWrappers/AdDetails/types';
import { PAGE } from '../../../helpers/pages';

import { CONVERT_GOAL_KEYS } from './convertGoalKeys';
import { useConvertLeadTracking } from './useConvertLeadTracking';

type AdDetailsLeadTrackingProps = {
  page: PAGE;
};

type AdDetailsLeadProps = {
  sectionName?: string;
  sellerType?: SellerTypes;
  price?: string | null;
};

const useAdDetailsLeadTracking = (props: AdDetailsLeadTrackingProps) => {
  const {
    publicRuntimeConfig: {
      GALLERY_INTERACTION_EXPERIMENT_STATUS,
      STICKY_SAVE_SHARE_EXPERIMENT_STATUS,
    },
  } = getConfig();
  const { trackLead } = useConvertLeadTracking(props);
  const { galleryInteractionsVariationKey, stickySaveShareVariationKey } =
    useConvertContext(props);

  const trackAdDetailsLead = useCallback(
    (leadProps: AdDetailsLeadProps) => {
      const { sectionName, sellerType, price } = leadProps;

      if (sectionName !== 'cars') return;

      const numericPrice = Number(String(price ?? '').replace(/,/g, ''));
      const hasValidPrice =
        price != null && !Number.isNaN(numericPrice) && numericPrice > 0;
      if (!hasValidPrice) return;

      const isGalleryInteractionBucketed =
        GALLERY_INTERACTION_EXPERIMENT_STATUS === 'active' &&
        galleryInteractionsVariationKey !== null;
      const isStickySaveShareVariationBucketed =
        STICKY_SAVE_SHARE_EXPERIMENT_STATUS === 'active' &&
        stickySaveShareVariationKey !== null;

      if (sellerType === SELLER_TYPES.PRIVATE) {
        if (
          isGalleryInteractionBucketed ||
          isStickySaveShareVariationBucketed
        ) {
          trackLead(CONVERT_GOAL_KEYS.PRIVATE_LEAD_GENERATED);
        }
        return;
      }

      if (sellerType !== SELLER_TYPES.DEALER) return;

      if (!isGalleryInteractionBucketed && !isStickySaveShareVariationBucketed)
        return;

      trackLead(CONVERT_GOAL_KEYS.PRO_LEAD_GENERATED);

      if (numericPrice >= 20000) {
        trackLead(CONVERT_GOAL_KEYS.PRO_LEAD_GENERATED_OVER_20K);
      }
    },
    [
      GALLERY_INTERACTION_EXPERIMENT_STATUS,
      STICKY_SAVE_SHARE_EXPERIMENT_STATUS,
      galleryInteractionsVariationKey,
      stickySaveShareVariationKey,
      trackLead,
    ],
  );

  return { trackAdDetailsLead };
};

export { useAdDetailsLeadTracking, type AdDetailsLeadProps };
